
/*
  KartWalk Automatic Product Image Slider
  Shopify Origin Theme

  Auto slide: Every 2 seconds
  Desktop + Mobile

  Removed:
  - 3 / 8 image counter
  - Custom square previous/next buttons

  Preserved:
  - Automatic image sliding
  - Mobile swiping
  - Product thumbnails
  - Variant image selection
  - Wishlist compatibility
*/

(function () {
  "use strict";

  const SLIDE_TIME = 2000;
  const galleries = new WeakMap();

  function initializeGallery(gallery) {
    if (galleries.has(gallery)) return;

    // Skip duplicate galleries.
    if (
      gallery.id &&
      gallery.id.endsWith("-duplicate")
    ) {
      return;
    }

    const viewer = gallery.querySelector(
      '[id^="GalleryViewer-"]'
    );

    const slider = gallery.querySelector(
      '[id^="Slider-Gallery-"]'
    );

    if (!viewer || !slider) return;

    const allSlides = Array.from(
      slider.querySelectorAll(
        ":scope > .product__media-item"
      )
    );

    // Auto-slide images only, not videos or 3D models.
    const imageSlides = allSlides.filter((slide) => {
      return (
        !slide.classList.contains(
          "product__media-item--full"
        ) &&
        !slide.classList.contains(
          "product__media-item--variant"
        ) &&
        !!slide.querySelector("img")
      );
    });

    // Remove any old custom counter and arrows.
    function removeOldControls() {
      gallery
        .querySelectorAll(
          ".kw-slider-controls, .kw-slider-counter"
        )
        .forEach((element) => element.remove());
    }

    removeOldControls();

    // Hide the native main gallery counter/buttons,
    // but do not hide thumbnail slider controls.
    gallery
      .querySelectorAll(
        '[id^="GalleryViewer-"] > .slider-buttons'
      )
      .forEach((element) => {
        element.style.setProperty(
          "display",
          "none",
          "important"
        );
      });

    if (imageSlides.length < 2) return;

    galleries.set(gallery, true);

    gallery.classList.add(
      "kw-auto-gallery",
      "kw-carousel-enabled"
    );

    let timer = null;
    let isHovering = false;
    let isTouching = false;
    let isFocused = false;
    let isPointerDown = false;
    let resumeTimeout = null;

    function activeSlide() {
      return (
        allSlides.find((slide) =>
          slide.classList.contains("is-active")
        ) || allSlides[0]
      );
    }

    function activeIndex() {
      const current = activeSlide();
      return imageSlides.indexOf(current);
    }

    function changeSlide(direction) {
      const current = activeIndex();

      let nextIndex;

      if (current === -1) {
        nextIndex =
          direction > 0
            ? 0
            : imageSlides.length - 1;
      } else {
        nextIndex =
          (
            current +
            direction +
            imageSlides.length
          ) % imageSlides.length;
      }

      const target = imageSlides[nextIndex];

      if (!target) return;

      if (
        typeof gallery.setActiveMedia === "function"
      ) {
        gallery.setActiveMedia(
          target.dataset.mediaId,
          false
        );
      } else {
        allSlides.forEach((slide) => {
          slide.classList.toggle(
            "is-active",
            slide === target
          );
        });

        slider.scrollTo({
          left:
            target.offsetLeft -
            slider.offsetLeft,
          behavior: "smooth"
        });
      }
    }

    function stopSlider() {
      if (timer !== null) {
        clearInterval(timer);
        timer = null;
      }
    }

    function shouldPause() {
      return (
        isHovering ||
        isTouching ||
        isFocused ||
        isPointerDown ||
        document.hidden ||
        !gallery.isConnected
      );
    }

    function startSlider() {
      stopSlider();

      if (shouldPause()) return;

      timer = window.setInterval(() => {
        if (shouldPause()) {
          stopSlider();
          return;
        }

        changeSlide(1);
      }, SLIDE_TIME);
    }

    function restartSlider() {
      stopSlider();

      if (resumeTimeout !== null) {
        clearTimeout(resumeTimeout);
      }

      resumeTimeout = window.setTimeout(() => {
        resumeTimeout = null;
        startSlider();
      }, 300);
    }

    // Pause when hovering over product gallery.
    gallery.addEventListener(
      "mouseenter",
      () => {
        isHovering = true;
        stopSlider();
      }
    );

    gallery.addEventListener(
      "mouseleave",
      () => {
        isHovering = false;
        startSlider();
      }
    );

    // Pause while touching/swiping on mobile.
    gallery.addEventListener(
      "touchstart",
      () => {
        isTouching = true;
        stopSlider();
      },
      { passive: true }
    );

    gallery.addEventListener(
      "touchend",
      () => {
        isTouching = false;
        restartSlider();
      },
      { passive: true }
    );

    gallery.addEventListener(
      "touchcancel",
      () => {
        isTouching = false;
        restartSlider();
      },
      { passive: true }
    );

    // Pause while dragging images.
    slider.addEventListener(
      "pointerdown",
      () => {
        isPointerDown = true;
        stopSlider();
      }
    );

    window.addEventListener(
      "pointerup",
      () => {
        if (isPointerDown) {
          isPointerDown = false;
          restartSlider();
        }
      }
    );

    window.addEventListener(
      "pointercancel",
      () => {
        if (isPointerDown) {
          isPointerDown = false;
          restartSlider();
        }
      }
    );

    // Keyboard accessibility.
    gallery.addEventListener(
      "focusin",
      () => {
        isFocused = true;
        stopSlider();
      }
    );

    gallery.addEventListener(
      "focusout",
      (event) => {
        if (
          !gallery.contains(event.relatedTarget)
        ) {
          isFocused = false;
          restartSlider();
        }
      }
    );

    // Pause when browser tab is hidden.
    document.addEventListener(
      "visibilitychange",
      () => {
        if (document.hidden) {
          stopSlider();
        } else {
          startSlider();
        }
      }
    );

    // Keep old counter elements removed if
    // another gallery update inserts them.
    const controlsObserver = new MutationObserver(
      () => {
        removeOldControls();
      }
    );

    controlsObserver.observe(viewer, {
      childList: true,
      subtree: true
    });

    // Start automatic product image sliding.
    startSlider();
  }

  function initializeAll() {
    document
      .querySelectorAll("media-gallery")
      .forEach(initializeGallery);
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      initializeAll
    );
  } else {
    initializeAll();
  }

  // Reinitialize in Shopify theme editor.
  document.addEventListener(
    "shopify:section:load",
    initializeAll
  );
})();
